import React, { useCallback, useEffect, useMemo, useRef, useState, useContext } from 'react';

import axios from 'axios';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import Service from '../../../lib/Service';
import { TestEngineRootContext } from '../../../contexts';
import RenderHtmlWithBlobs from '../RenderHtmlWithBlob';
import VimeoPlayer from '../VimeoPlayer';


const getAudioExtFromMimeType = (mimeType) => {
  if (!mimeType) return 'webm';
  const cleaned = String(mimeType).split(';')[0].trim();
  const ext = cleaned.split('/')[1];
  return ext || 'webm';
};

const RecordAudio = ({
  question,
  studentResponse,
  setUploadEssayAnswer,
  frameTrack,
  handleUploadAndNext
}) => {

  const MAX_RECORDING_SECONDS = question?.maxRecordingSeconds || 180;
  const TOTAL_TIME_MS = MAX_RECORDING_SECONDS * 1000;

  const studentAns = useMemo(
    () =>
      studentResponse.find(
        (t) =>
          t.sectionid === question?.QuestionPaperSectionID &&
          t.itemId === question?.ItemID,
      ),
    [question?.ItemID, question?.QuestionPaperSectionID, studentResponse],
  );

  const savedAudioUrl = studentAns?.enteredText || null;

  const audioRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const isUnmountingRef = useRef(false);

  const [isRecording, setIsRecording] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(TOTAL_TIME_MS);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedUrl, setRecordedUrl] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [audioError, setAudioError] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const { showQuitModal } = useContext(TestEngineRootContext);

  const audioSrc = savedAudioUrl || '';

  const cleanupObjectUrl = useCallback(() => {
    if (recordedUrl && recordedUrl.startsWith('blob:')) {
      URL.revokeObjectURL(recordedUrl);
    }
  }, [recordedUrl]);

  const stopTracks = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const stopRecordingIfAny = useCallback(() => {
    try {
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = null;
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state !== 'inactive'
      ) {
        mediaRecorderRef.current.stop();
      }
    } catch (e) {
      // noop
    }
  }, []);

  useEffect(() => {
    isUnmountingRef.current = false;
    cleanupObjectUrl();
    setRecordedBlob(null);
    setRecordedUrl(null);
    setIsRecording(false);
    setTimeRemaining(TOTAL_TIME_MS);
    setIsSaving(false);
    setAudioError(null);
    setIsPlaying(false);
    stopRecordingIfAny();
    stopTracks();

    return () => {
      isUnmountingRef.current = true;
      stopRecordingIfAny();
      stopTracks();
      cleanupObjectUrl();
    };
  }, [question?.ItemID]);

  const startTimer = useCallback(() => {
    const startTime = Date.now();

    timerRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, TOTAL_TIME_MS - elapsed);

      setTimeRemaining(remaining);

      if (remaining <= 0 && mediaRecorderRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
        if (mediaRecorderRef.current.state !== 'inactive') {
          mediaRecorderRef.current.stop();
        }
      }
    }, 100);
  }, []);

  const stopTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
  }, []);

  const pauseAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
      try {
        audioRef.current.currentTime = 0;
      } catch (e) {
        // noop
      }
    }
  }, []);

  const startRecording = useCallback(async () => {
    setAudioError(null);

    if (!navigator?.mediaDevices?.getUserMedia) {
      setAudioError('Microphone is not supported in this browser.');
      return;
    }
    if (typeof window.MediaRecorder === 'undefined') {
      setAudioError('Audio recording is not supported in this browser.');
      return;
    }

    pauseAudio();

    try {
      cleanupObjectUrl();
      setRecordedBlob(null);
      setRecordedUrl(null);

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      chunksRef.current = [];

      const mimeCandidates = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/ogg;codecs=opus',
        'audio/ogg',
      ];
      const mimeType =
        mimeCandidates.find((t) => window.MediaRecorder.isTypeSupported(t)) ||
        '';

      const recorder = new window.MediaRecorder(
        stream,
        mimeType ? { mimeType } : undefined,
      );
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = async () => {
        stopTimer();
        stopTracks();
        setIsRecording(false);

        const finalMimeType = recorder.mimeType || mimeType || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type: finalMimeType });
        setRecordedBlob(blob);

        cleanupObjectUrl();
        const url = URL.createObjectURL(blob);
        setRecordedUrl(url);

        setIsSaving(true);
        setAudioError(null);

        try {
          const ext = getAudioExtFromMimeType(blob.type);
          const objectName = `recordAudio_${question?.ItemID}_${Date.now()}.${ext}`;

          const s3Response = await Service.GET({
            name: 'file-upload',
            queryString: `contentType=uploadEssay&objectName=${objectName}`,
            testengine: true,
          });

          if (!s3Response?.success || !s3Response?.data?.signedUrl) {
            throw new Error(s3Response?.message || 'Failed to get upload URL');
          }

          const { signedUrl } = s3Response.data;
          if (!signedUrl) throw new Error('Failed to get uploaded file URL');

          const prevAuth = axios.defaults.headers.common.Authorization;
          delete axios.defaults.headers.common.Authorization;

          await axios.put(signedUrl, blob, {
            headers: {
              'Content-Type': blob.type || 'application/octet-stream',
            },
            withCredentials: false,
          });

          axios.defaults.headers.common.Authorization = prevAuth;
          setUploadEssayAnswer(question, signedUrl);
          frameTrack(null, question);

          cleanupObjectUrl();
          setRecordedBlob(null);
          setRecordedUrl(null);

          if (audioRef.current) {
            pauseAudio();
          }

          if (!isUnmountingRef.current) {
            handleUploadAndNext();
          }

        } catch (err) {
          setAudioError(err?.message || 'Failed to save recording.');
        } finally {
          setIsSaving(false);
        }

        setTimeRemaining(TOTAL_TIME_MS);
      };

      recorder.start();
      setIsRecording(true);
      setTimeRemaining(TOTAL_TIME_MS);
      startTimer();
    } catch (err) {
      stopTracks();
      stopTimer();
      setIsRecording(false);
      setAudioError(err?.message || 'Failed to start recording.');
    }
  }, [
    cleanupObjectUrl,
    pauseAudio,
    startTimer,
    stopTracks,
    stopTimer,
  ]);

  const stopRecording = useCallback(() => {
    if (!mediaRecorderRef.current) return;
    stopRecordingIfAny();
  }, [stopRecordingIfAny]);

  useEffect(() => {
    if (showQuitModal && isRecording) {
      stopRecording();
    }
  }, [showQuitModal, isRecording, stopRecording]);

  const leftHtml = question?.Items || '';
  const videoURL = question?.questionVideoURL;
  const isVimeoVideo = question?.questionVideoURL && question?.questionVideoURL.includes('vimeo');

  return (
    <div className="type-record-audio-wrapper">
      <div className="qn-question">
        {leftHtml && (
          <RenderHtmlWithBlobs htmlString={leftHtml} />
        )}
        {isVimeoVideo && videoURL && (
          <VimeoPlayer
            vimeoLink={videoURL}
            onVideoComplete={() => { }}
          />
        )}
      </div>

      <div className="qn-recorder">
        <div className="recorder-header">
          {!savedAudioUrl && (
            <p className="recorder-subtitle">
              {question?.recordInstructions || 'Listen to question video, record your response by clicking'}{' '}
              <b>Start Recording</b>
            </p>
          )}
        </div>

        {audioError && (
          <div
            className="recorder-error"
            role="alert"
          >
            {audioError}
          </div>
        )}

        {audioSrc && (
          <audio
            ref={audioRef}
            src={audioSrc}
            onEnded={() => setIsPlaying(false)}
          />
        )}

        <div className="recorder-buttons">
          {!savedAudioUrl ? (
            // Recording mode — no answer yet
            <>
              {!isRecording && (
                <button
                  type="button"
                  className="mob-btn-submit"
                  style={{
                    background: '#2b6cb0',
                    color: '#fff',
                    border: '1px solid #2b6cb0',
                    borderRadius: 2,
                    minWidth: 160,
                    padding: '8px 14px',
                    fontSize: 14,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    justifyContent: 'center',
                  }}
                  onClick={startRecording}
                >
                  <i
                    className="fa fa-microphone"
                    aria-hidden="true"
                    style={{ fontSize: 14, lineHeight: 1 }}
                  />
                  Start Recording
                </button>
              )}

              {isRecording && (
                <button
                  type="button"
                  className="mob-btn-submit"
                  style={{
                    background: '#e53e3e',
                    color: '#fff',
                    border: '1px solid #e53e3e',
                    borderRadius: 2,
                    minWidth: 160,
                    padding: '8px 14px',
                    fontSize: 14,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    justifyContent: 'center',
                  }}
                  onClick={stopRecording}
                >
                  <i
                    className="fa fa-stop"
                    aria-hidden="true"
                  />
                  Stop Recording
                </button>
              )}

              <div
                className="recorder-timer"
                style={{ marginTop: 10 }}
              >
                {isRecording ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <div
                      style={{
                        fontSize: 16,
                        fontWeight: 'bold',
                        color: timeRemaining <= 30000 ? '#e53e3e' : '#2b6cb0'
                      }}>
                      ⏱️ {Math.floor(timeRemaining / 1000)}s remaining
                    </div>
                  </div>
                ) : recordedBlob ? (
                  isSaving ? 'Saving...' : 'Recording saved; moving to next question...'
                ) : null}
              </div>
            </>
          ) : (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
                padding: '4px 0',
              }}
            >
              <div style={{ fontSize: 14, color: '#333' }}>
                Your audio response has been recorded and submitted.
              </div>
              <div style={{ fontSize: 12, color: '#666' }}>
                Recording is locked; you cannot re‑record this question.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

RecordAudio.propTypes = {
  question: PropTypes.object.isRequired,
  studentResponse: PropTypes.array.isRequired,
  setUploadEssayAnswer: PropTypes.func.isRequired,
  frameTrack: PropTypes.func.isRequired,
  handleUploadAndNext: PropTypes.func.isRequired
};

const mapStateToProps = ({ engine }) => ({
  studentResponse: engine.studentResponse,
});

export default connect(mapStateToProps)(RecordAudio);